import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  ClockIcon,
  ArrowPathIcon,
  QuestionMarkCircleIcon,
  ClipboardDocumentListIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  XCircleIcon,
  StopCircleIcon,
} from '@heroicons/react/24/solid';
import apiService from '../services/api';
import { ImportStatus } from '../types/api';
import { getApiErrorMessage } from '../constants/messages';
import Logger from '../utils/logger';
import { useTranslation } from 'react-i18next';

interface ImportProgressModalProps {
  isOpen: boolean;
  importId: string | null;
  onClose: () => void;
  onSuccess: () => void;
  onError: (error: string) => void;
}

const ImportProgressModal: React.FC<ImportProgressModalProps> = ({
  isOpen,
  importId,
  onClose,
  onSuccess,
  onError
}) => {
  const { t } = (useTranslation as any)('builds');
  const [importStatus, setImportStatus] = useState<ImportStatus | null>(null);
  const [logs, setLogs] = useState<string>('');
  const [showLogs, setShowLogs] = useState(true);
  const [isCancelling, setIsCancelling] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const durationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstPollRef = useRef(true);

  const formatLogsFromDict = useCallback((logsDict: Record<string, string[]>): string => {
    if (!logsDict || Object.keys(logsDict).length === 0) {
      return t('importProgress.noLogs') as string;
    }

    let formattedLogs = '';
    Object.entries(logsDict).forEach(([taskName, logLines]) => {
      const displayName = taskName.charAt(0).toUpperCase() + taskName.slice(1);
      formattedLogs += `\n=== ${displayName} ===\n`;
      logLines.forEach(line => {
        formattedLogs += line + '\n';
      });
    });

    return formattedLogs;
  }, []);

  useEffect(() => {
    if (isOpen && importId) {
      setFailedAttempts(0);
      setElapsedTime(0);
      setImportStatus(null);
      setLogs('');
      isFirstPollRef.current = true;
      startPolling();
    }

    return () => {
      stopPolling();
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
      }
    };
  }, [isOpen, importId]);

  // Real-time duration tracking
  useEffect(() => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }

    if (importStatus?.status === 'importing' && importStatus.start_time) {
      const updateElapsed = () => {
        const start = new Date(importStatus.start_time!).getTime();
        const now = Date.now();
        setElapsedTime(Math.floor((now - start) / 1000));
      };

      updateElapsed();
      durationTimerRef.current = setInterval(updateElapsed, 1000);
    } else if (importStatus?.start_time && importStatus?.completion_time) {
      const start = new Date(importStatus.start_time).getTime();
      const end = new Date(importStatus.completion_time).getTime();
      setElapsedTime(Math.floor((end - start) / 1000));
    }

    return () => {
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
      }
    };
  }, [importStatus?.status, importStatus?.start_time, importStatus?.completion_time]);

  const startPolling = () => {
    if (!importId) return;

    const pollStatus = async () => {
      try {
        const status = await apiService.getImportStatus(importId);
        setImportStatus(status);
        setFailedAttempts(0);

        // Load logs
        try {
          const logsData = await apiService.getImportLogs(importId);
          const formattedLogs = formatLogsFromDict(logsData.logs);
          setLogs(formattedLogs);
        } catch (logsError) {
          Logger.warn('Impossible de charger les logs d\'import:', logsError);
        }

        // Handle terminal states
        const isTerminal = ['succeeded', 'failed', 'cancelled'].includes(status.status);
        const isReview = isFirstPollRef.current && isTerminal;
        isFirstPollRef.current = false;

        if (isTerminal) {
          stopPolling();
        }

        if (status.status === 'succeeded' && !isReview) {
          toast.success(t('importProgress.importSuccessToast') as string);
          onSuccess();
        } else if (status.status === 'failed' && !isReview) {
          const errorMsg = status.error_message || t('importProgress.importFailedDefault') as string;
          toast.error(errorMsg);
          onError(errorMsg);
        }
      } catch (error: unknown) {
        Logger.error('Erreur lors du polling import:', error);
        setFailedAttempts(prev => prev + 1);

        if (failedAttempts >= 10) {
          toast.error(t('importProgress.apiConnectionError') as string);
        }
      }
    };

    pollStatus();
    pollingIntervalRef.current = setInterval(pollStatus, 3000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'bg-surface-3 text-text-secondary';
      case 'importing':
        return 'bg-blue-500/12 text-blue-400';
      case 'succeeded':
        return 'bg-green-500/12 text-green-400';
      case 'failed':
        return 'bg-red-500/12 text-red-400';
      case 'cancelled':
        return 'bg-yellow-500/12 text-yellow-400';
      default:
        return 'bg-surface-3 text-text-secondary';
    }
  };

  const getStatusIcon = (status: string): React.ReactNode => {
    switch (status) {
      case 'pending': return <ClockIcon className="w-5 h-5" />;
      case 'importing': return <ArrowPathIcon className="w-5 h-5" />;
      case 'succeeded': return <CheckCircleIcon className="w-5 h-5" />;
      case 'failed': return <XCircleIcon className="w-5 h-5" />;
      case 'cancelled': return <StopCircleIcon className="w-5 h-5" />;
      default: return <QuestionMarkCircleIcon className="w-5 h-5" />;
    }
  };

  const getStatusText = (status: string): string => {
    switch (status) {
      case 'pending': return t('importProgress.pending') as string;
      case 'importing': return t('importProgress.importing') as string;
      case 'succeeded': return t('importProgress.succeeded') as string;
      case 'failed': return t('importProgress.failed') as string;
      case 'cancelled': return t('importProgress.cancelled') as string;
      default: return t('importProgress.unknown') as string;
    }
  };

  const getProgressPercentage = (): number => {
    if (!importStatus) return 0;

    switch (importStatus.status) {
      case 'pending': return 10;
      case 'importing': {
        // Time-based interpolation
        if (importStatus.start_time) {
          const elapsed = (Date.now() - new Date(importStatus.start_time).getTime()) / 1000;
          return Math.min(90, Math.round(15 + 75 * (1 - Math.exp(-elapsed / 120))));
        }
        return 30;
      }
      case 'succeeded':
      case 'failed':
      case 'cancelled':
        return 100;
      default:
        return 0;
    }
  };

  const formatDuration = (seconds?: number): string => {
    if (seconds === undefined || seconds === null) return '-';
    if (seconds === 0) return '0s';

    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const formatBytes = (bytes?: number): string => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const handleCancelImport = async () => {
    if (!importId) return;

    if (!window.confirm(t('importProgress.cancelConfirm') as string)) {
      return;
    }

    try {
      setIsCancelling(true);
      await apiService.cancelImport(importId);
      toast.success(t('importProgress.cancelSuccess') as string);
    } catch (error: unknown) {
      Logger.error('Erreur lors de l\'annulation:', error);
      toast.error(getApiErrorMessage(error) || 'Erreur lors de l\'annulation de l\'import');
    } finally {
      setIsCancelling(false);
    }
  };

  if (!isOpen || !importId) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center" role="dialog" aria-modal="true" aria-labelledby="import-progress-title">
      <div className="relative bg-surface-2 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-border-1">
          <div>
            <h3 id="import-progress-title" className="text-2xl font-bold text-text-primary flex items-center">
              <ArchiveBoxIcon className="w-6 h-6 inline mr-2" /> {t('importProgress.title')}
              {importStatus && (
                <span className={`ml-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(importStatus.status)}`}>
                  {getStatusIcon(importStatus.status)} {getStatusText(importStatus.status)}
                </span>
              )}
            </h3>
            <p className="text-sm text-text-tertiary mt-1">
              Import ID: <span className="font-mono">{importId}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label={t('common:close') as string}
            className="text-text-quaternary hover:text-text-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2 rounded"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Progress Bar */}
          <div>
            <div className="flex justify-between text-sm text-text-secondary mb-2">
              <span>{t('importProgress.progression')}</span>
              <span>{getProgressPercentage()}%</span>
            </div>
            <div className="w-full bg-surface-4 rounded-full h-3 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  importStatus?.status === 'succeeded'
                    ? 'bg-green-500'
                    : importStatus?.status === 'failed'
                    ? 'bg-red-500'
                    : 'bg-accent-500'
                } ${importStatus?.status === 'importing' ? 'animate-pulse' : ''}`}
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
          </div>

          {/* Status Info */}
          {importStatus && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-surface-3 rounded-lg p-3">
                <div className="text-xs text-text-tertiary mb-1">{t('importProgress.status')}</div>
                <div className="text-sm font-medium text-text-primary">
                  {getStatusText(importStatus.status)}
                </div>
              </div>

              <div className="bg-surface-3 rounded-lg p-3">
                <div className="text-xs text-text-tertiary mb-1">{t('importProgress.duration')}</div>
                <div className="text-sm font-medium text-text-primary flex items-center">
                  {importStatus.status === 'importing' && (
                    <span className="w-2 h-2 bg-accent-500 rounded-full animate-pulse mr-2" />
                  )}
                  {formatDuration(elapsedTime)}
                </div>
              </div>

              {importStatus.source_image && (
                <div className="bg-surface-3 rounded-lg p-3">
                  <div className="text-xs text-text-tertiary mb-1">{t('importProgress.sourceImage')}</div>
                  <div className="text-sm font-medium text-text-primary font-mono truncate" title={importStatus.source_image}>
                    {importStatus.source_image}
                  </div>
                </div>
              )}

              {importStatus.start_time && (
                <div className="bg-surface-3 rounded-lg p-3">
                  <div className="text-xs text-text-tertiary mb-1">{t('importProgress.started')}</div>
                  <div className="text-sm font-medium text-text-primary">
                    {new Date(importStatus.start_time).toLocaleTimeString('fr-FR')}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {importStatus?.status === 'failed' && importStatus.error_message && (
            <div className="p-4 bg-red-500/12 border border-red-500/30 rounded-lg">
              <div className="flex items-start">
                <XCircleIcon className="w-6 h-6 text-red-500 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-red-400 mb-1">{t('importProgress.importError')}</h4>
                  <p className="text-xs text-red-400 font-mono">{importStatus.error_message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Success Message */}
          {importStatus?.status === 'succeeded' && (
            <div className="p-4 bg-green-500/12 border border-green-500/30 rounded-lg">
              <div className="flex items-start">
                <CheckCircleIcon className="w-6 h-6 text-green-500 mr-3 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-green-400 mb-2">{t('importProgress.importSuccess')}</h4>
                  <p className="text-xs text-green-400 mb-2">
                    {t('importProgress.importSuccessDesc')}
                  </p>
                  <div className="space-y-2">
                    {importStatus.target_image && (
                      <div className="bg-surface-2 p-2 rounded border border-green-500/30">
                        <p className="text-xs text-text-tertiary mb-1">{t('importProgress.targetImage')}</p>
                        <p className="text-xs font-mono text-text-secondary break-all">
                          {importStatus.target_image}
                        </p>
                      </div>
                    )}
                    {importStatus.image_digest && (
                      <div className="bg-surface-2 p-2 rounded border border-green-500/30">
                        <p className="text-xs text-text-tertiary mb-1">{t('importProgress.digest')}</p>
                        <p className="text-xs font-mono text-text-secondary break-all">
                          {importStatus.image_digest}
                        </p>
                      </div>
                    )}
                    {importStatus.import_size_bytes && (
                      <div className="bg-surface-2 p-2 rounded border border-green-500/30">
                        <p className="text-xs text-text-tertiary mb-1">{t('importProgress.size')}</p>
                        <p className="text-xs font-mono text-text-secondary">
                          {formatBytes(importStatus.import_size_bytes)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Logs Section */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-sm font-medium text-text-secondary flex items-center"><ClipboardDocumentListIcon className="w-4 h-4 inline mr-1" /> {t('importProgress.logsTitle')}</h4>
              <button
                onClick={() => setShowLogs(!showLogs)}
                className="text-sm text-accent-400 hover:text-accent-300 font-medium"
              >
                {showLogs ? <><ChevronUpIcon className="w-4 h-4 inline mr-1" /> {t('importProgress.hide')}</> : <><ChevronDownIcon className="w-4 h-4 inline mr-1" /> {t('importProgress.show')}</>}
              </button>
            </div>

            {showLogs && (
              <div className="bg-gray-900 rounded-lg p-4 font-mono text-xs border border-gray-700">
                <pre className="text-green-400 whitespace-pre-wrap break-words">
                  {logs || <><ClockIcon className="w-4 h-4 inline mr-1" /> {t('importProgress.loadingLogs')}</>}
                </pre>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-border-1">
          <div>
            {/* Cancel button - only during active import */}
            {(importStatus?.status === 'pending' || importStatus?.status === 'importing') && (
              <button
                onClick={handleCancelImport}
                disabled={isCancelling}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isCancelling ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('importProgress.cancelling')}
                  </>
                ) : (
                  <><StopCircleIcon className="w-4 h-4 inline mr-1" /> {t('importProgress.cancelImport')}</>
                )}
              </button>
            )}
          </div>

          <button
            onClick={onClose}
            className="px-6 py-2 bg-surface-4 text-text-secondary rounded-md hover:bg-surface-4 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportProgressModal;
